import { PageHero } from "../components/PageHero";
import { Section, SectionHead, Card } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../sections/CTASection";
import { site } from "../data/site";

/**
 * /about
 *
 * Written under a hard constraint from the brief and from the project's own
 * rules: invent nothing. No team size, no office count beyond the two we
 * have, no revenue, no awards, no partnerships, no customer names.
 *
 * What is left is more interesting than what was removed. A company can be
 * specific about how it works without being specific about how big it is,
 * and the commitments below are all things we control rather than things
 * we would have to be believed about.
 */

const PRINCIPLES = [
  {
    t: "We say what the software actually does",
    b: "This site once claimed every attendance punch carried photo, GPS and server time. It does not — the product deliberately records a punch with a missing photo and flags it, because discarding it would delete the evidence that someone was on site. The weaker sentence was the true one, so the weaker sentence is what is on the page now.",
  },
  {
    t: "The people who reply are the people who build",
    b: "Your first email is not read by a sales team and passed to a delivery team. There is no handover between the person who scopes the work and the person who writes the code, which removes the place most of the misunderstanding usually lives.",
  },
  {
    t: "We would rather lose the work than oversell it",
    b: "If the honest answer is that you do not need what you asked for, or that we are not the right people for it, we say so in the first conversation rather than the third invoice.",
  },
  {
    t: "We operate what we build",
    b: "Hazri Book is run by us, in production, for a paying customer, in a market with a regulator. Everything we believe about architecture came from being on call for our own decisions — not from reading about someone else's.",
  },
];

export function About() {
  return (
    <>
      <PageHero
        eyebrow="About"
        title={
          <>
            A technology company{" "}
            <span className="text-gradient">that ships its own software.</span>
          </>
        }
        lede={`Astalabista is an India-registered technology company, founded in ${site.foundedYear}, based in ${site.locations.join(" and ")}. We advise, we engineer, and we build and operate our own SaaS products.`}
        primary={{ href: "/contact", label: "Start a conversation" }}
        secondary={{ href: "/work", label: "See what we have built" }}
      />

      <Section tight>
        <div className="container">
          <Reveal>
            <div className="max-w-3xl space-y-5 text-[15px] leading-relaxed text-white/70 sm:text-lg">
              <p>
                We started by building products rather than by selling
                services, which is an unusual order and it shaped everything
                after it. Agent Labista came out of watching property sales
                floors lose leads in the minutes nobody picked up the phone.
                Hazri Book came out of a contracting company's month-end, where
                the argument was never about the work done but about what could
                be proven.
              </p>
              <p>
                Hazri Book is live and ours to operate — which means we have
                been paged by our own architecture, we have had to migrate our
                own schema with real data in it, and we have had to explain our
                own outage to a customer. Agent Labista is still in the lab.
                That operating experience is the thing we actually sell, and it
                is why our advice sounds different from advice given by people
                who hand a system over and leave.
              </p>
              <p>
                We work with startups, established businesses and enterprises,
                in India and across the Gulf. We are deliberately not trying to
                be a large services company. We take work we can do well, we
                say no to the rest, and we would rather be the small firm a CTO
                calls back than the big one they had to.
              </p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section>
        <div className="container">
          <SectionHead
            eyebrow="What you can hold us to"
            title={
              <>
                Four commitments,{" "}
                <span className="text-gradient">all of them checkable.</span>
              </>
            }
            lede="Not values. Values are unfalsifiable. These are things you can catch us failing at."
          />

          <div className="mt-10 grid gap-4 sm:mt-14 sm:grid-cols-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={(i % 2) * 0.07}>
                <Card className="h-full p-6 sm:p-7">
                  <div className="font-mono text-[11px] tabular-nums text-white/25">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 font-display text-base font-semibold leading-snug tracking-tight">
                    {p.t}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/62">{p.b}</p>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      <Section tight>
        <div className="container">
          <Reveal>
            <div className="grid gap-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:grid-cols-3 sm:p-8">
              <Fact label="Registered" value="India" />
              <Fact label="Founded" value={site.foundedYear} />
              <Fact label="Presence" value={site.locations.join(" · ")} />
              <Fact label="In production" value="Hazri Book" />
              <Fact label="Production market" value="Saudi Arabia" />
              <Fact label="Contact" value={site.email} href={`mailto:${site.email}`} />
            </div>
          </Reveal>
        </div>
      </Section>

      <CTASection
        title={
          <>
            Want to know if we are{" "}
            <span className="text-gradient">right for this?</span>
          </>
        }
        lede="One conversation is usually enough to tell. Describe the problem and we will tell you honestly whether it is ours to solve."
        primary={{ href: "/contact", label: "Start a conversation" }}
        secondary={{ href: "/what-we-do", label: "See how we engage" }}
      />
    </>
  );
}

function Fact({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
        {label}
      </div>
      {href ? (
        <a
          href={href}
          className="mt-1.5 block break-words text-sm font-medium text-white transition-colors hover:text-emerald-300"
        >
          {value}
        </a>
      ) : (
        <div className="mt-1.5 text-sm font-medium text-white">{value}</div>
      )}
    </div>
  );
}
